import React from 'react';
import { BookOpen, Users, Calendar, CheckCircle, ChevronRight } from 'lucide-react';

const SubjectCard = ({ subject, onClick }) => {
  const getEligibilityColor = (eligibleCount, totalStudents) => {
    const percentage = totalStudents > 0 ? (eligibleCount / totalStudents) * 100 : 0;
    if (percentage >= 80) return 'text-emerald-700 bg-emerald-50 border-emerald-200/60';
    if (percentage >= 60) return 'text-amber-700 bg-amber-50 border-amber-200/60';
    return 'text-rose-700 bg-rose-50 border-rose-200/60';
  };

  const getCieColor = (avgCie) => {
    if (avgCie >= 75) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (avgCie >= 60) return 'text-amber-600 bg-amber-50 border-amber-100';
    if (avgCie >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-100';
    return 'text-rose-600 bg-rose-50 border-rose-100';
  };

  const getAttendanceColor = (avgAttendance) => {
    if (avgAttendance >= 75) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (avgAttendance >= 60) return 'text-amber-600 bg-amber-50 border-amber-100';
    return 'text-rose-600 bg-rose-50 border-rose-100';
  };

  const eligiblePercentage = subject.totalStudents > 0 
    ? (subject.eligibleCount / subject.totalStudents) * 100 
    : 0;

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-slate-300 active:scale-[0.99] md:active:scale-100 transition-all duration-200 cursor-pointer flex flex-col justify-between group h-full"
    >
      <div>
        {/* Card Header */}
        <div className="bg-slate-50/70 px-4 py-3.5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-indigo-600 shrink-0" />
              <h3 className="font-bold text-slate-900 text-sm sm:text-base truncate group-hover:text-indigo-600 transition-colors">
                {subject.subjectName}
              </h3>
            </div>
            
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span className="text-[10px] font-mono font-bold tracking-wider text-slate-500 bg-slate-200/60 px-1.5 py-0.5 rounded">
                {subject.subjectCode}
              </span>
              {subject.isLab && (
                <span className="text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded">
                  Laboratory Ledger
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-4 space-y-4">
          {/* Main Roster Stats Grid */}
          <div className="grid grid-cols-2 gap-2.5 bg-slate-50/50 border border-slate-100 rounded-xl p-2.5 text-center">
            <div>
              <span className="flex items-center justify-center gap-1 text-slate-400 font-semibold text-[10px] uppercase tracking-wider">
                <Users className="w-3 h-3" />
                Enrolled
              </span>
              <p className="text-xl font-black text-slate-800 mt-1">
                {subject.totalStudents || 0}
              </p>
            </div>
            
            <div className="border-l border-slate-200/60">
              <span className="flex items-center justify-center gap-1 text-slate-400 font-semibold text-[10px] uppercase tracking-wider">
                <CheckCircle className="w-3 h-3" />
                Clearance
              </span>
              <div className="mt-1">
                <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold border ${getEligibilityColor(subject.eligibleCount, subject.totalStudents)}`}>
                  {subject.eligibleCount || 0} / {subject.totalStudents || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Bar for Eligibility */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs font-medium">
              <span className="text-slate-400">Total Eligibility Index</span>
              <span className="text-slate-700 font-bold">{Math.round(eligiblePercentage)}%</span>
            </div>
            <div className="w-full bg-slate-100 border border-slate-200/40 rounded-full h-1.5 overflow-hidden">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all duration-500"
                style={{ width: `${eligiblePercentage}%` }}
              />
            </div>
          </div>

          {/* CIE and Attendance Averages */}
          <div className="grid grid-cols-1 gap-2 pt-1">
            <div className="flex items-center justify-between text-xs sm:text-sm font-medium">
              <span className="text-slate-500">CIE Average</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getCieColor(subject.avgCiePercentage || 0)}`}>
                {subject.avgCiePercentage || 0}%
              </span>
            </div>
            
            <div className="flex items-center justify-between text-xs sm:text-sm font-medium">
              <span className="text-slate-500">Attendance Aggregate</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getAttendanceColor(subject.avgAttendance || 0)}`}>
                {subject.avgAttendance || 0}%
              </span>
            </div>
          </div>

          {/* Faculty Info Metadata */}
          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
              <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate">
                Instructor: <strong className="text-slate-600 font-semibold">{subject.facultyName || 'Unassigned Catalog'}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* View Action Footer */}
      <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 group-hover:bg-indigo-50/50 border-transparent transition-colors mt-2">
        <span className="w-full text-center text-xs sm:text-sm font-bold text-slate-600 group-hover:text-indigo-600 flex items-center justify-center gap-1">
          Open Marks Ledger
          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 text-slate-400 group-hover:text-indigo-500" />
        </span>
      </div>
    </div>
  );
};

export default SubjectCard;